import React, {useState} from 'react';
import {capitalize} from 'lodash';
import {Alert, Box, Grid, Typography} from '@mui/material';
import {Formik} from 'formik';
import {useSnackbar} from 'notistack';
import gql from 'graphql-tag';
import {useMutation} from '@apollo/client';
import ActionModal from './ActionModal';
import {ListContacts_contacts} from '../../schema-types/ListContacts';

import validateUUID from '../../utils/validateUUID';
import {GET_FPA_DOCUMENTS, useFPAdocuments} from './useFPAdocuments';
import Loading from '../Loading/Loading';
import {ErrorPage} from '../../pages/ErrorPage';
import {themeColors} from '../../theme/Theme';
import {
  FPACancellationReasonEnum,
  OneviewDocumentSignatureRequestStatusChoices,
} from '../../schema-types/globalTypes';
import {getContactName} from '../../utils/getContactName';
import AutoField, {ActionType, SelectField} from '../AutoForm/AutoFieldNew';
import {newUTCDate} from '../../utils/newUTCDate';

import {AccountBasicFragment} from '../../schema-types/AccountBasicFragment';
import saltus from '../../shared/logger';
import {GetLatestFPA_latestFpa} from '../../schema-types/GetLatestFPA';
import {
  CancelFpaSignatureRequest,
  CancelFpaSignatureRequestVariables,
} from '../../schema-types/CancelFpaSignatureRequest';
import {useAutoField} from '../AutoForm/useAutoForm';
import {Button} from '../Button/Button';

const templateField: SelectField = {
  id: 'documentTemplate',
  label: 'Select Document Template',
  type: ActionType.select,
  options: [],
};

export const PENDING_MESSAGE =
  "If the client record isn't yet available on client portal, the request can't effectively be finalised and will remain in a pending state indefinitely.";

export const getFpaStatusColour = (
  status: OneviewDocumentSignatureRequestStatusChoices
) => {
  switch (status) {
    case OneviewDocumentSignatureRequestStatusChoices.FAILED:
      return themeColors.lightAlert;
    case OneviewDocumentSignatureRequestStatusChoices.PENDING:
      return themeColors.greyLight;
    case OneviewDocumentSignatureRequestStatusChoices.SENT:
      return themeColors.lightBlue;
    case OneviewDocumentSignatureRequestStatusChoices.SIGNED:
      return themeColors.lightGreen;
    default:
      return themeColors.lightAlert;
  }
};

const RequestFPASignatureModal: React.FC<{
  account: AccountBasicFragment;
  contact: ListContacts_contacts;
  isOpen: boolean;
  close: () => void;
}> = ({account, contact, isOpen, close}) => {
  const accountId = validateUUID(account?.id);
  const contactId = validateUUID(contact?.id);
  const {enqueueSnackbar} = useSnackbar();
  const {
    latestFpa,
    fpaError,
    fpaLoading,
    getSignature,
    mutationError,
    mutationLoading,
    documentSignaturesTemplates,
    documentSignaturesTemplatesLoading,
    documentSignaturesTemplatesError,
    resendSignature,
  } = useFPAdocuments({
    accountId,
    contactId,
  });
  const [isSending, setIsSending] = useState(false);
  // Fold the templates query into the loading/error guards. Without this the
  // modal becomes interactive as soon as the latestFpa query resolves, even if
  // the template list is still loading or errored — letting the adviser submit
  // with no template and send a blank documentFileName (ON-9275).
  const loading = fpaLoading || mutationLoading || documentSignaturesTemplatesLoading;
  const error = fpaError || mutationError || documentSignaturesTemplatesError;
  const documentTemplateInitialValue = documentSignaturesTemplates[0]?.fileName || '';
  const isPending =
    latestFpa?.status === OneviewDocumentSignatureRequestStatusChoices.PENDING;

  return (
    <ActionModal
      title="Financial Planning Agreement"
      isOpen={isOpen}
      close={close}
      wide
      autoHeight
      key={`${latestFpa?.id}-${latestFpa?.status}-${isSending}`}
    >
      <Formik
        initialValues={{documentTemplate: ''}}
        onSubmit={async (theseValues) => {
          setIsSending(true);
          if (!accountId || !contactId) {
            return;
          }
          if (latestFpa) {
            await resendSignature({
              variables: {
                accountId,
                contactId,
                documentName: 'Financial Planning Agreement',
              },
            });
            saltus.log('on send', latestFpa?.status);
            const message = `Financial Planning Agreement sent at ${newUTCDate().toLocaleDateString(
              'en-GB',
              {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }
            )}`;
            enqueueSnackbar(message, {variant: 'success'});
          } else {
            const documentFileName =
              theseValues.documentTemplate || documentTemplateInitialValue;
            if (!documentFileName) {
              // Guard against ON-9275: never send a blank template name — the
              // portal 404s on it and the request is recorded as failed.
              enqueueSnackbar('Please select a document template before sending.', {
                variant: 'error',
              });
              setIsSending(false);
              return;
            }
            await getSignature({
              variables: {
                accountId,
                contactId,
                /**
                 * Note: this bit is confusing as it currently stands.
                 * Theoretically, a joint scope means that the same document is produced twice,
                 * and won't be considered signed until all the parties have signed it.
                 * Unfortunately it doesn't result in a duplication of the entry in the portal db table,
                 * so until we have that it's better to default to Single scope and
                 * have the user go through both contacts as we do with ATRs.
                 */
                scope: 'Single',
                documentName: 'Financial Planning Agreement',
                documentFileName,
              },
            });
          }
          setIsSending(false);
        }}
      >
        {({setFieldValue, handleSubmit, values}) => {
          return (
            <form onSubmit={handleSubmit} key={values.documentTemplate}>
              {loading && <Loading />}
              {error && (
                <ErrorPage
                  code="Server error"
                  text={fpaError?.message || mutationError?.message || 'FPA error'}
                />
              )}
              <Box p={2} pb={2}>
                <Grid spacing={3} container>
                  {!loading && !error && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="h4">
                          Request FPA Signature for {getContactName(contact)}
                        </Typography>
                        <Box flex={1} mt={2} mr={1} mb={2}>
                          <AutoField
                            field={{
                              ...templateField,
                              initialValue: documentTemplateInitialValue,
                              options:
                                documentSignaturesTemplates.map((x) => {
                                  return {
                                    value: x?.fileName || '',
                                    label: x?.fileName || '',
                                  };
                                }) ?? [],
                            }}
                          />
                        </Box>
                        <Button
                          eventName="Send FPA"
                          variant="contained"
                          color="primary"
                          type="submit"
                          disabled={
                            isSending ||
                            // For a new request a template must be available
                            // and selected; a re-send reuses the stored one.
                            (!latestFpa &&
                              !(values.documentTemplate || documentTemplateInitialValue))
                          }
                        >
                          {`${
                            latestFpa
                              ? 'Re-send Financial Planning Agreement'
                              : 'Request signature'
                          }`}
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="h4">Current Status</Typography>
                        {latestFpa ? (
                          <ExistingFPADisplay
                            isPending={isPending}
                            contact={contact}
                            latestFpa={latestFpa}
                          />
                        ) : (
                          <Typography mt={2} fontSize={14}>
                            No signature requests have been sent for{' '}
                            {getContactName(contact)}
                          </Typography>
                        )}
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </form>
          );
        }}
      </Formik>
    </ActionModal>
  );
};

const cancelFPAMutation = gql`
  mutation CancelFpaSignatureRequest(
    $documentSignatureId: UUID!
    $reason: FPACancellationReasonEnum!
  ) {
    cancelFpaSignatureRequest(
      documentSignatureId: $documentSignatureId
      reason: $reason
    ) {
      ok
      errors
    }
  }
`;

const cancelReasonField: SelectField = {
  id: 'cancelReason',
  fullWidth: true,
  options: [
    {
      label: 'Cancel - Signed in other Media',
      value: FPACancellationReasonEnum.OTHER_MEDIA,
    },
    {
      label: 'Cancel - Client Not Proceeding',
      value: FPACancellationReasonEnum.CLIENT_REFUSED,
    },
  ],
  label: 'Cancellation reason',
  type: ActionType.select,
};

const ExistingFPADisplay: React.FC<{
  isPending: boolean;
  contact: ListContacts_contacts;
  latestFpa: GetLatestFPA_latestFpa;
}> = ({isPending, contact, latestFpa}) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [{value: cancelReason}] = useAutoField<FPACancellationReasonEnum | null>(
    'cancelReason'
  );
  const [onCancelFPAMutation, {loading: cancelLoading}] = useMutation<
    CancelFpaSignatureRequest,
    CancelFpaSignatureRequestVariables
  >(cancelFPAMutation, {
    refetchQueries: [GET_FPA_DOCUMENTS],
    awaitRefetchQueries: true,
  });
  const onCancel = async () => {
    if (!cancelReason) {
      enqueueSnackbar('Please select a cancellation reason', {variant: 'error'});
      return;
    }
    enqueueSnackbar('Removing FPA Signature request...', {variant: 'info'});
    onCancelFPAMutation({
      variables: {
        documentSignatureId: latestFpa.id,
        reason: cancelReason,
      },
    })
      .then(({data, errors}) => {
        closeSnackbar();
        if (data?.cancelFpaSignatureRequest?.ok) {
          enqueueSnackbar('Removed FPA Signature request', {variant: 'success'});
        } else {
          throw Error('Unable to cancel FPA');
        }
      })
      .catch(() => {
        closeSnackbar();
        enqueueSnackbar('Unable to remove FPA Signature request', {variant: 'info'});
      });
  };
  const statusDisplay = isPending ? 'Pending signature' : latestFpa?.status;
  return (
    <>
      <Box mt={2} display="flex" flexDirection="column">
        <Box
          mb={2}
          borderRadius="5px"
          bgcolor={getFpaStatusColour(latestFpa.status)}
          p={1.5}
        >
          <Typography fontSize={14}>
            Status: <strong>{capitalize(statusDisplay)}</strong>
            {isPending && <Alert severity="warning">{PENDING_MESSAGE}</Alert>}
          </Typography>
        </Box>
        <Box mb={1}>
          <Typography>
            Name: <strong>{getContactName(contact)}</strong>
          </Typography>
        </Box>
        <Box mb={1}>
          <Typography>
            Date Created: {newUTCDate(latestFpa.dateCreated).toLocaleDateString('en-GB')}
          </Typography>
        </Box>
        {latestFpa.status === OneviewDocumentSignatureRequestStatusChoices.SIGNED &&
          latestFpa.dateSigned && (
            <Box mb={1}>
              <Typography>
                Date Signed:{' '}
                {newUTCDate(latestFpa.dateSigned).toLocaleDateString('en-GB')}
              </Typography>
            </Box>
          )}
        <Box mb={1}>
          <Typography>Signed: {latestFpa.signed ? 'Yes' : 'No'}</Typography>
        </Box>
        <Box>
          <Typography>
            <strong>Cancel FPA Signature request:</strong>
          </Typography>
          <Box mt={1} mb={1}>
            <AutoField field={cancelReasonField} />
          </Box>
          <Button
            variant="contained"
            color="secondary"
            eventName="Cancel FPA"
            disabled={cancelLoading}
            onClick={() => onCancel()}
          >
            Cancel FPA
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default RequestFPASignatureModal;
